import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import css from './YandexMapSelector.module.css';

const YandexMapSelector = ({ 
    initialLat, 
    initialLng, 
    onLocationSelect,
    markers = [],
    onMarkerClick,
    readOnly = false,
    disableTooltips = false,
    showCount = false,
    hoveredDelivery = null,
    courierRoutes = []
}) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const searchInputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const [coordinates, setCoordinates] = useState({ lat: initialLat, lng: initialLng });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedAddress, setSelectedAddress] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef(null);
    const [ymapsLoaded, setYmapsLoaded] = useState(false);
    const markersRef = useRef(new Map());
    const routesRef = useRef(new Map());
    const [selectedMarker, setSelectedMarker] = useState(null);

    // Function to load the Yandex Maps API
    const loadYandexMapsAPI = () => {
        return new Promise((resolve, reject) => {
            // If API is already loaded, resolve immediately
            if (window.ymaps && window.ymaps.ready && typeof window.ymaps.Map === 'function') {
                setYmapsLoaded(true);
                resolve(window.ymaps);
                return;
            }

            // Create script element
            const script = document.createElement('script');
            script.type = 'text/javascript';
            // Use environment variable for API key
            script.src = `https://api-maps.yandex.ru/2.1/?apikey=${process.env.REACT_APP_YANDEX_MAPS_API_KEY}&lang=en_US`;
            script.async = true;

            // Handle script load
            script.onload = () => {
                if (window.ymaps) {
                    window.ymaps.ready(() => {
                        setYmapsLoaded(true);
                        resolve(window.ymaps);
                    });
                } else {
                    reject(new Error('Yandex Maps API failed to load'));
                }
            };

            // Handle script error
            script.onerror = () => {
                reject(new Error('Failed to load Yandex Maps API script'));
            };

            // Add script to document
            document.head.appendChild(script);
        });
    };

    // Function to get address from coordinates
    const getAddressFromCoordinates = async (lat, lng) => {
        if (!window.ymaps || !ymapsLoaded) return;

        try {
            const geocoder = window.ymaps.geocode([lat, lng]);
            const result = await geocoder;

            if (result.geoObjects.getLength() > 0) {
                const address = result.geoObjects.get(0).getAddressLine();
                setSelectedAddress(address);
            } else {
                setSelectedAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            }
        } catch (error) {
            console.error('Reverse geocoding error:', error);
            setSelectedAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        }
    };

    // Function to search for address using geocode
    const searchAddress = async (query) => {
        if (!window.ymaps || !ymapsLoaded || query.trim().length < 3 || isSearching) {
            return;
        }

        setIsSearching(true);
        console.log("Searching for address:", query);

        try {
            // Use geocode to find addresses
            const geocodeResult = await window.ymaps.geocode(query, { results: 5 });
            
            if (geocodeResult.geoObjects.getLength() > 0) {
                const suggestions = [];
                
                // Process each result
                for (let i = 0; i < Math.min(5, geocodeResult.geoObjects.getLength()); i++) {
                    const geoObject = geocodeResult.geoObjects.get(i);
                    const coords = geoObject.geometry.getCoordinates();
                    const address = geoObject.getAddressLine();
                    
                    suggestions.push({
                        displayName: address,
                        value: address,
                        coords: coords,
                    });
                }
                
                console.log("Found suggestions:", suggestions);
                setSuggestions(suggestions);
                setShowSuggestions(suggestions.length > 0);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error('Error in geocode:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        }

        setIsSearching(false);
    };

    // Handle search input change
    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear any existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set a new timeout to search after typing stops
        if (query.trim().length >= 3) {
            searchTimeoutRef.current = setTimeout(() => {
                searchAddress(query);
            }, 300);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (suggestion) => {
        // Update search query with selected suggestion
        setSearchQuery(suggestion.displayName);

        // Hide suggestions
        setShowSuggestions(false);

        // Update map and placemark
        if (mapInstanceRef.current && markerRef.current) {
            const [lat, lng] = suggestion.coords;

            // Update map center and placemark position
            mapInstanceRef.current.setCenter(suggestion.coords, 15);
            markerRef.current.geometry.setCoordinates(suggestion.coords);

            // Update state
            setCoordinates({ lat, lng });
            setSelectedAddress(suggestion.displayName);

            // Call the callback
            onLocationSelect(lat, lng);
        }
    };

    // Initialize map
    const initMap = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Load the API
            await loadYandexMapsAPI();

            // Wait for ymaps to be fully loaded
            if (!ymapsLoaded) {
                console.log('Waiting for Yandex Maps API to be fully loaded...');
                return;
            }

            // Create map instance
            const map = new window.ymaps.Map(mapRef.current, {
                center: [initialLat, initialLng],
                zoom: 12,
                controls: ['zoomControl', 'fullscreenControl']
            });

            mapInstanceRef.current = map;

            if (!readOnly) {
                // Create main editable marker
                const marker = new window.ymaps.Placemark([initialLat, initialLng], {}, {
                    draggable: true
                });

                // Add marker to map
                map.geoObjects.add(marker);
                markerRef.current = marker;

                // Get initial address
                getAddressFromCoordinates(initialLat, initialLng);

                // Handle marker drag
                marker.events.add('dragend', () => {
                    const coords = marker.geometry.getCoordinates();
                    updateCoordinates(coords[0], coords[1]);
                });

                // Handle map click
                map.events.add('click', (e) => {
                    const coords = e.get('coords');
                    marker.geometry.setCoordinates(coords);
                    updateCoordinates(coords[0], coords[1]);
                });
            }

            // Add delivery markers
            markers.forEach(marker => {
                let placemark;
                
                // Special handling for start point marker
                if (marker.id === 'start-point') {
                    placemark = new window.ymaps.Placemark(
                        [marker.lat, marker.lng],
                        {
                            balloonContent: marker.balloonContent,
                            hintContent: marker.hintContent
                        },
                        {
                            preset: 'islands#blackIcon'
                        }
                    );
                } else {
                    // Determine marker color based on courier assignment
                    const markerColor = marker.courier ? '#1E88E5' : '#F44336'; // Blue if courier assigned, red if not
                    
                    if (showCount && marker.count) {
                        // Create an SVG with the count
                        const svgWithCount = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="12" fill="${markerColor}"/>
                            <text x="12" y="16" font-family="Arial" font-size="12" font-weight="bold" fill="white" text-anchor="middle">${marker.count}</text>
                        </svg>`;
                        
                        // Create a custom icon with the SVG
                        placemark = new window.ymaps.Placemark(
                            [marker.lat, marker.lng],
                            {
                                // Only set balloonContent and hintContent if disableTooltips is false
                                ...(disableTooltips ? {} : {
                                    balloonContent: marker.balloonContent || marker.title,
                                    hintContent: marker.hintContent || marker.address
                                })
                            },
                            {
                                iconLayout: 'default#image',
                                iconImageHref: 'data:image/svg+xml;utf8,' + encodeURIComponent(svgWithCount),
                                iconImageSize: [24, 24],
                                iconImageOffset: [-12, -12]
                            }
                        );
                    } else {
                        // Create a standard marker
                        placemark = new window.ymaps.Placemark(
                            [marker.lat, marker.lng],
                            {
                                // Only set balloonContent and hintContent if disableTooltips is false
                                ...(disableTooltips ? {} : {
                                    balloonContent: marker.balloonContent || marker.title,
                                    hintContent: marker.hintContent || marker.address
                                })
                            },
                            {
                                preset: marker.courier ? 'islands#darkBlueIcon' : 'islands#redIcon'
                            }
                        );
                    }

                    // Add delivery order number if courier is assigned
                    if (marker.courier) {
                        // Find the courier's route to determine delivery order
                        const courierRoute = courierRoutes.find(route => route.courier === marker.courier);
                        if (courierRoute && courierRoute.points) {
                            // Find the order of this delivery in the route (excluding start point)
                            const deliveryOrder = courierRoute.points.findIndex(point => 
                                Math.abs(point.lat - marker.lat) < 0.0001 && 
                                Math.abs(point.lng - marker.lng) < 0.0001
                            );
                            
                            if (deliveryOrder > 0) { // Skip start point (index 0)
                                // Create a small numbered marker positioned next to the main marker
                                const orderSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                                    <circle cx="10" cy="10" r="10" fill="${courierRoute.color}" stroke="white" stroke-width="2"/>
                                    <text x="10" y="14" font-family="Arial" font-size="12" font-weight="bold" fill="white" text-anchor="middle">${deliveryOrder}</text>
                                </svg>`;
                                
                                const orderPlacemark = new window.ymaps.Placemark(
                                    [marker.lat + 0.0002, marker.lng + 0.0002], // Slight offset from main marker
                                    {
                                        hintContent: `Delivery order: ${deliveryOrder}`
                                    },
                                    {
                                        iconLayout: 'default#image',
                                        iconImageHref: 'data:image/svg+xml;utf8,' + encodeURIComponent(orderSvg),
                                        iconImageSize: [20, 20],
                                        iconImageOffset: [-10, -10],
                                        zIndex: 100 // Ensure it appears above other markers
                                    }
                                );
                                
                                map.geoObjects.add(orderPlacemark);
                                markersRef.current.set(`${marker.id}-order`, orderPlacemark);
                            }
                        }
                    }
                }

                // Add click handler
                if (onMarkerClick) {
                    placemark.events.add('click', () => {
                        setSelectedMarker(marker);
                        onMarkerClick(marker);
                    });
                }

                map.geoObjects.add(placemark);
                markersRef.current.set(marker.id, placemark);
            });

            // Add courier routes
            courierRoutes.forEach((route) => {
                if (route.points && route.points.length > 1) {
                    // Convert points to Yandex Maps format
                    const routeCoordinates = route.points.map(point => [point.lat, point.lng]);
                    
                    // Create polyline for the route
                    const polyline = new window.ymaps.Polyline(
                        routeCoordinates,
                        {
                            balloonContent: `Courier: ${route.courier} (${route.deliveryCount} deliveries)`,
                            hintContent: `${route.courier} route`
                        },
                        {
                            strokeColor: route.color,
                            strokeWidth: 4,
                            strokeOpacity: 0.8
                        }
                    );

                    map.geoObjects.add(polyline);
                    routesRef.current.set(route.courier, polyline);
                }
            });

            // Store map instance
            mapInstanceRef.current = map;
            setIsLoading(false);
            
            // Force resize after map is fully initialized to ensure it fills container properly
            setTimeout(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.container.fitToViewport();
                }
            }, 100);
        } catch (err) {
            console.error('Error initializing map:', err);
            setError(`Error initializing map: ${err.message}`);
            setIsLoading(false);
        }
    };

    // Update coordinates
    const updateCoordinates = (lat, lng) => {
        setCoordinates({ lat, lng });
        getAddressFromCoordinates(lat, lng);
        onLocationSelect(lat, lng);
    };

    // Initialize map on component mount
    useEffect(() => {
        initMap();

        // Cleanup on unmount
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.destroy();
            }
        };
    }, []);

    // Re-initialize map when ymaps is loaded
    useEffect(() => {
        if (ymapsLoaded && !mapInstanceRef.current) {
            initMap();
        }
    }, [ymapsLoaded]);

    // Handle window resize to ensure map takes full height
    useEffect(() => {
        const handleResize = () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.container.fitToViewport();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Handle marker highlighting for hovered delivery
    useEffect(() => {
        if (!mapInstanceRef.current) return;

        // Reset all markers to normal state first
        markersRef.current.forEach((marker, markerId) => {
            const markerData = markers.find(m => `${m.lat},${m.lng}` === markerId);
            if (markerData) {
                // Reset to original state
                if (showCount && markerData.count) {
                    // Regenerate original SVG
                    const originalColor = markerData.courier ? '#1E88E5' : '#F44336';
                    const originalSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="12" fill="${originalColor}"/>
                        <text x="12" y="16" font-family="Arial" font-size="12" font-weight="bold" fill="white" text-anchor="middle">${markerData.count}</text>
                    </svg>`;
                    marker.options.set('iconImageHref', 'data:image/svg+xml;utf8,' + encodeURIComponent(originalSvg));
                    marker.options.set('iconImageSize', [24, 24]);
                    marker.options.set('iconImageOffset', [-12, -12]);
                } else {
                    // Reset preset marker
                    marker.options.set('preset', markerData.courier ? 'islands#darkBlueIcon' : 'islands#redIcon');
                }
            }
        });

        // If there's a hovered delivery, highlight its marker
        if (hoveredDelivery && hoveredDelivery.latitude && hoveredDelivery.longitude) {
            const hoveredMarkerId = `${hoveredDelivery.latitude},${hoveredDelivery.longitude}`;
            const hoveredMarker = markersRef.current.get(hoveredMarkerId);

            if (hoveredMarker) {
                // Find the corresponding marker data
                const markerData = markers.find(m => `${m.lat},${m.lng}` === hoveredMarkerId);
                
                if (showCount && markerData?.count) {
                    // For SVG markers, create green version
                    const greenSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="12" fill="#4CAF50"/>
                        <text x="12" y="16" font-family="Arial" font-size="12" font-weight="bold" fill="white" text-anchor="middle">${markerData.count}</text>
                    </svg>`;
                    hoveredMarker.options.set('iconImageHref', 'data:image/svg+xml;utf8,' + encodeURIComponent(greenSvg));
                } else {
                    // For preset markers, change to green
                    hoveredMarker.options.set('preset', 'islands#greenIcon');
                }
            }
        }
    }, [hoveredDelivery, markers, showCount]);

    // Handle courier routes updates
    useEffect(() => {
        if (!mapInstanceRef.current) return;

        // Clear existing routes
        routesRef.current.forEach((route) => {
            mapInstanceRef.current.geoObjects.remove(route);
        });
        routesRef.current.clear();

        // Add new routes
        courierRoutes.forEach((route) => {
            if (route.points && route.points.length > 1) {
                // Convert points to Yandex Maps format
                const routeCoordinates = route.points.map(point => [point.lat, point.lng]);
                
                // Create polyline for the route
                const polyline = new window.ymaps.Polyline(
                    routeCoordinates,
                    {
                        balloonContent: `Courier: ${route.courier} (${route.deliveryCount} deliveries)`,
                        hintContent: `${route.courier} route`
                    },
                    {
                        strokeColor: route.color,
                        strokeWidth: 4,
                        strokeOpacity: 0.8
                    }
                );

                mapInstanceRef.current.geoObjects.add(polyline);
                routesRef.current.set(route.courier, polyline);
            }
        });
    }, [courierRoutes]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchInputRef.current &&
                !searchInputRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={css.mapWrapper}>
            {isLoading && <div className={css.loading}>Loading map...</div>}
            {error ? (
                <div className={css.errorMessage}>
                    {error}
                </div>
            ) : (
                <div className={css.mapContent}>
                    {!readOnly && (
                        <div className={css.searchContainer}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search for an address..."
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                onFocus={() => {
                                    if (suggestions.length > 0) {
                                        setShowSuggestions(true);
                                    }
                                }}
                                className={css.searchInput}
                            />
                            {showSuggestions && (
                                <div ref={suggestionsRef} className={css.suggestions}>
                                    {suggestions.length > 0 ? (
                                        suggestions.map((suggestion, index) => (
                                            <div
                                                key={`${suggestion.value}-${index}`}
                                                className={css.suggestionItem}
                                                onClick={() => handleSuggestionSelect(suggestion)}
                                            >
                                                {suggestion.displayName}
                                            </div>
                                        ))
                                    ) : (
                                        <div className={css.noSuggestions}>
                                            {isSearching ? 'Searching...' : 'No suggestions found'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <div ref={mapRef} className={css.mapContainer} />
                    {!readOnly && (
                        <div className={css.coordinates}>
                            <div className={css.address}>
                                {selectedAddress || `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`}
                            </div>
                            <div className={css.coords}>
                                Latitude: {coordinates.lat.toFixed(6)}, Longitude: {coordinates.lng.toFixed(6)}
                            </div>
                            {selectedMarker && (
                                <div className={css.courierInfo}>
                                    <div className={css.courierTitle}>
                                        <strong>Delivery Information:</strong>
                                    </div>
                                    {selectedMarker.title && (
                                        <div className={css.courierField}>
                                            <strong>Title:</strong> {selectedMarker.title}
                                        </div>
                                    )}
                                    {selectedMarker.address && (
                                        <div className={css.courierField}>
                                            <strong>Address:</strong> {selectedMarker.address}
                                        </div>
                                    )}
                                    <div className={css.courierField}>
                                        <strong>Courier:</strong> {selectedMarker.courier ? (
                                            <span className={css.courierAssigned}>{selectedMarker.courier}</span>
                                        ) : (
                                            <span className={css.courierNotAssigned}>Not assigned</span>
                                        )}
                                    </div>
                                    {selectedMarker.additionalInfo && (
                                        <div className={css.courierField}>
                                            <strong>Additional Info:</strong> {selectedMarker.additionalInfo}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

YandexMapSelector.propTypes = {
    initialLat: PropTypes.number.isRequired,
    initialLng: PropTypes.number.isRequired,
    onLocationSelect: PropTypes.func.isRequired,
    markers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
        title: PropTypes.string,
        address: PropTypes.string
    })),
    onMarkerClick: PropTypes.func,
    readOnly: PropTypes.bool,
    disableTooltips: PropTypes.bool,
    showCount: PropTypes.bool,
    hoveredDelivery: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        clientName: PropTypes.string,
        assignedCourier: PropTypes.string
    }),
    courierRoutes: PropTypes.arrayOf(PropTypes.shape({
        courier: PropTypes.string.isRequired,
        points: PropTypes.arrayOf(PropTypes.shape({
            lat: PropTypes.number.isRequired,
            lng: PropTypes.number.isRequired
        })).isRequired,
        color: PropTypes.string.isRequired,
        deliveryCount: PropTypes.number.isRequired
    }))
};

export { YandexMapSelector }; 